import { getContentType } from "@/utils/getContentType";
import { getImageSrc2x } from "@/utils/imageUrl";
import { useMemoizedFn } from "ahooks";
import classNames from "classnames";
import { debounce, upperCase } from "lodash";
import Link from "next/link";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import AiTag from "../AiTag";
import HBIcon from "../HBIcon";
import HBSwiper from "../HBSwiper";
import Image from "../Image";
import styles from "./index.module.less";

function HorizontalSimilarMaterialList({
  material_id,
  pin_id,
  onSelect,
  className = "",
  style = {},
  similiarList,
  isNew,
  isFull = false,
  searchWord,
  showSub = true,
  rowsNum = 2,
}) {
  const [visable, setVisable] = useState(true);
  const { data, error, loading } = similiarList;

  const renderListData = data?.pins || [];

  // 渲染单个素材
  const render = useMemoizedFn(
    (pin: IPin, options: { width: number; height: number }) => {
      const { width, height } = options;
      return (
        <div key={pin.pin_id} className={styles.card}>
          <a
            className={styles.item}
            href={`/pins/${pin.pin_id}`}
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey) {
                return;
              }
              e.preventDefault();
              const currentSrc =
                e.currentTarget.querySelector("img").currentSrc;
              onSelect(pin, currentSrc, "similarMaterial");
            }}
            data-gd-expose="content_expose"
            data-gd-click="material_click"
            data-module-name="相似商用素材"
            data-content-id={pin.pin_id}
            data-content-type={getContentType(pin)}
            data-material-id={pin.file_material?.material_id}
            data-board-id={pin.board_id}
            data-file-id={pin.file_id}
            data-search-word={searchWord}
            data-ext={JSON.stringify(pin?.ext)}
            data-aigc
          >
            <Image
              onLoad={(evt) => {
                const target = evt.target;
                target?.classList.add("transparent-img-bg");
              }}
              {...getImageSrc2x(pin.file, { width: Math.max(width, 100) })}
              height={height}
              width={width}
              alt={pin.raw_text}
              showError={true}
            />
            <AiTag pin={pin} size="sm" />

            {rowsNum === 1 && (
              <div className={styles.property}>
                {pin.file_material.material_category} |{" "}
                {upperCase(pin.file_material.material_type)}
              </div>
            )}
          </a>
        </div>
      );
    }
  );

  useEffect(() => {
    if (!loading) {
      const renderListData = data?.pins || [];
      if (error || data?.err || renderListData.length === 0) {
        setVisable(false);
        if (error || data?.err) {
          console.error(error || data?.err);
        }
      } else {
        setVisable(true);
      }
    }
  }, [data, error, loading]);

  const horizontalListRef = useRef<HTMLDivElement>(null);
  const swiperRef = useRef(null);
  const [page, setPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    function calculatePage() {
      if (horizontalListRef.current) {
        const scrollDom = swiperRef.current.getScrollDom();
        const viewWidth = scrollDom.clientWidth;
        const innerWidth = scrollDom.scrollWidth;
        setPage(Math.ceil(innerWidth / viewWidth));
      }
    }

    const debounceCalculatePage = debounce(calculatePage, 100);

    debounceCalculatePage();

    window.addEventListener("resize", debounceCalculatePage);
    return () => {
      window.removeEventListener("resize", debounceCalculatePage);
    };
  }, []);

  const scrollToPage = (page: number) => {
    const scrollDom = swiperRef.current.getScrollDom();
    const viewWidth = scrollDom.clientWidth;

    const target = page * viewWidth;
    swiperRef.current.scrollTo(target);
    setCurrentPage(page);
  };

  const onScroll = useMemoizedFn((left: number) => {
    const scrollDom = swiperRef.current.getScrollDom();
    const viewWidth = scrollDom.clientWidth;
    const currentPage = Math.ceil(Math.abs(left) / viewWidth);
    setCurrentPage(currentPage);
  });

  const rowStyle = useMemo(() => {
    return {
      gap: rowsNum === 1 ? `16px` : `8px`,
    };
  }, [rowsNum]);

  // 渲染横向数据，支持多行展示
  const renderRows = useMemoizedFn(() => {
    const rowWidth = Array.from({ length: rowsNum }, () => 0);
    const rows = Array.from({ length: rowsNum }, () => []);
    // 一行展示220，其他展示200高度
    const rowHeight = rowsNum === 1 ? 220 : 200;

    renderListData.forEach((pin: IPin) => {
      // 找到宽度最小的行
      const minWidth = Math.min(...rowWidth);
      const minWidthIndex = rowWidth.indexOf(minWidth);

      const imageWidth = (rowHeight * pin.file.width) / pin.file.height;

      // 计算行宽
      rowWidth[minWidthIndex] += imageWidth;

      // 添加到行
      rows[minWidthIndex].push(
        render(pin, {
          width: imageWidth,
          height: rowHeight,
        })
      );
    });

    return Array.from({ length: rowsNum }).map((_, rowIndex) => {
      return (
        <div key={rowIndex} className={styles.row} style={rowStyle}>
          {rows[rowIndex]}
        </div>
      );
    });
  });

  return (
    <>
      {visable && (
        <div
          className={classNames(
            className,
            isNew && styles.isNew,
            isFull && styles.isFull,
            styles.horizontal,
            rowsNum === 1 && styles.singleRow
          )}
          style={{
            ...style,
          }}
        >
          <div className={styles.similar} data-content-source="相似商用素材">
            <div className={styles.header}>
              <Link href={`/pins/${pin_id}/similar/materials`}>
                <a
                  target={"_blank"}
                  className={classNames(styles.title, {
                    [styles.withIcon]: showSub,
                  })}
                  data-gd-click="button_click"
                  data-button-name="更多相似商用素材"
                  data-content-source="相似商用素材-标题"
                  data-material-id={material_id}
                  data-module-name="相似商用素材"
                  data-search-word={searchWord}
                  rel="nofollow"
                >
                  相似商用素材
                </a>
              </Link>
              {showSub ? (
                <span className={styles.sub}>源文件可下载编辑</span>
              ) : (
                <span style={{ flex: 1 }} />
              )}
              <Link href={`/pins/${pin_id}/similar/materials`}>
                <a
                  target={"_blank"}
                  className={styles.more}
                  data-gd-click="button_click"
                  data-button-name="更多相似商用素材"
                  data-content-source="相似商用素材-顶部"
                  data-material-id={material_id}
                  data-module-name="相似商用素材"
                  data-search-word={searchWord}
                  rel="nofollow"
                >
                  更多
                  <HBIcon type="ic_chevron_right" />
                </a>
              </Link>
            </div>

            <HBSwiper
              ref={swiperRef}
              iconClassName={styles.swiperIcon}
              hideGradient
              onScroll={onScroll}
            >
              <div className={styles.horizontalList} ref={horizontalListRef}>
                {/* 根据展示行数，渲染不同的数据，2行时，单数第一行，双数第二行 */}
                {renderRows()}
              </div>
            </HBSwiper>

            <div className={styles.pages}>
              {Array.from({ length: page }).map((_, index) => (
                <div
                  key={index}
                  className={classNames(styles.circle, {
                    [styles.current]: currentPage === index,
                  })}
                  onClick={() => scrollToPage(index)}
                ></div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
export default memo(HorizontalSimilarMaterialList);
